import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Form, Row, Col, Alert, Card } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ManagementList from '../components/ManagementList';
import { InfoCircle } from 'react-bootstrap-icons';
import DeviceSettings from '../components/DeviceSettings/DeviceSettings';
import CustomDateRangeFilter from '../components/CustomDateRangeFilter/CustomDateRangeFilter';
import {
  exportEnrollmentStatusReport,
  fetchEntityDropDownList,
  fetchClientDropDownList,
  fetchEnrollmentStatusReport,
  getDeviceDeeplink,
} from '../services/api';
import { getCurrentUser } from '../services/authService';
import { LIMIT, SUBSCRIPTION_TYPES, USER_ROLES } from '../lib/utils/constant';
import { usePermissions } from '../Hooks/usePermission';
import { PERMISSIONS } from '../lib/utils/permissions';

const REPORT_CODE_ENROLLMENT = 'customer_enrolment';

const subscriptionStatuses = [
  { value: '', label: 'Select...' },
  { value: SUBSCRIPTION_TYPES.ACTIVE, label: 'Active' },
  { value: SUBSCRIPTION_TYPES.TRIALING, label: 'Trialing' },
  { value: SUBSCRIPTION_TYPES.PAST_DUE, label: 'Past Due' },
  { value: SUBSCRIPTION_TYPES.CANCELLED, label: 'Cancelled' },
];

function Reports() {
  const today = useMemo(() => new Date(), []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  }, []);
  const formatDate = (dateValue) => dateValue.toISOString().slice(0, 10);
  const parseDateValue = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [hasRunReport, setHasRunReport] = useState(false);
  const [reportRows, setReportRows] = useState([]);
  const [reportColumns, setReportColumns] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(LIMIT[0].value);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [sortConfigs, setSortConfigs] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsIframeUrl, setSettingsIframeUrl] = useState('');
  const [showSettingsIframe, setShowSettingsIframe] = useState(false);
  const [settingsIframeLoading, setSettingsIframeLoading] = useState(false);
  const [selectedSettingsDeviceId, setSelectedSettingsDeviceId] = useState(null);
  const [selectedSettingsUserName, setSelectedSettingsUserName] = useState('');
  const [selectedSettingsSimNumber, setSelectedSettingsSimNumber] = useState('');
  const [entitiesList, setEntitiesList] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [filters, setFilters] = useState({
    entityId: '',
    clientId: '',
    subscriptionStatus: '',
    periodFrom: formatDate(thirtyDaysAgo),
    periodTo: formatDate(today),
  });
  const [visibility, setVisibility] = useState({
    entity: true,
    client: true,
  });
const {can} = usePermissions();
const currentUser = useMemo(() => getCurrentUser(), []);
const canExportEnrollmentReport = can(PERMISSIONS.REPORT_CUSTOMER_ENROLMENT_EXPORT, currentUser?.role?.code);
  const reportCode = searchParams.get('code');
  const isEnrollmentFlow = reportCode === REPORT_CODE_ENROLLMENT;

  useEffect(() => {
    const accountType = currentUser?.entityMaster?.accountType;
    const userEntityId = currentUser?.entityMaster?.id;
    const parentEntityId = currentUser?.entityMaster?.parent_id;

    if (accountType === USER_ROLES.CLIENT) {
      setVisibility({ entity: false, client: false });
      setFilters((prev) => ({
        ...prev,
        entityId: parentEntityId ? String(parentEntityId) : '',
        clientId: userEntityId ? String(userEntityId) : '',
      }));
      return;
    }

    if (accountType === USER_ROLES.ENTITY) {
      setVisibility({ entity: false, client: true });
      setFilters((prev) => ({
        ...prev,
        entityId: userEntityId ? String(userEntityId) : '',
      }));
      return;
    }

    setVisibility({ entity: true, client: true });
  }, [currentUser]);

  useEffect(() => {
    const loadEntities = async () => {
      setLoadingEntities(true);
      try {
        const response = await fetchEntityDropDownList();
        const items = response?.data?.data?.items || [];
        setEntitiesList(items);
      } catch (error) {
        console.error('Error loading entities for report filters:', error);
        setEntitiesList([]);
      } finally {
        setLoadingEntities(false);
      }
    };

    loadEntities();
  }, []);

  useEffect(() => {
    if (!filters.entityId) {
      setLoadingClients(false);
      setAvailableClients([]);
      setFilters((prev) => {
        const nextClientId = visibility.client ? '' : prev.clientId;
        return prev.clientId === nextClientId ? prev : { ...prev, clientId: nextClientId };
      });
      return;
    }

    if(currentUser?.role?.code === USER_ROLES.CASE_MANAGER) {
      setVisibility({ entity: false, client: true });
      setAvailableClients([{id: currentUser?.entityMaster?.id, name: currentUser?.entityMaster?.name}]);
      return;
    }

    let isCancelled = false;
    const loadClients = async () => {
      setLoadingClients(true);
      try {
        const response = await fetchClientDropDownList(filters.entityId);
        const clients = response?.data?.data?.items || [];
        if (isCancelled) return;
        setAvailableClients(clients);
        if (visibility.client && filters.clientId && !clients.some((client) => client.id === Number(filters.clientId))) {
          setFilters((prev) => ({ ...prev, clientId: '' }));
        }
      } catch (error) {
        if (isCancelled) return;
        console.error('Error loading clients for report filters:', error);
        setAvailableClients([]);
      } finally {
        if (!isCancelled) setLoadingClients(false);
      }
    };

    loadClients();

    return () => {
      isCancelled = true;
    };
  }, [filters.entityId, visibility.client, filters.clientId]);
  useEffect(() => {
    if (reportCode === REPORT_CODE_ENROLLMENT) {
      setShowFiltersPanel(true);
      setHasRunReport(false);
      setReportRows([]);
      setReportColumns([]);
      setReportError('');
      setPage(1);
      setTotalPages(1);
      setSortConfig({ key: null, direction: 'asc' });
      setSortConfigs([]);
    }
  }, [reportCode]);

  const closeEnrollmentStatusFilters = () => {
    setShowFiltersPanel(false);
    setSearchParams({});
    navigate('/');
  };

  const closeSettingsModal = () => {
    setShowSettingsModal(false);
    setSettingsIframeUrl('');
    setShowSettingsIframe(false);
    setSettingsIframeLoading(false);
    setSelectedSettingsDeviceId(null);
    setSelectedSettingsUserName('');
    setSelectedSettingsSimNumber('');
  };
  const handleOpenDeviceSettings = (item) => {
    const deviceId = item?.__deviceId ?? item?.__sourceId ?? item?.id;
    if (deviceId == null) {
      setReportError('No device is linked to this record.');
      return;
    }
    const rowName = item?.customer_name || '';
    const simCandidates = [
      item?.__simNumber,
      item?.msisdn,
      item?.sim_number,
      item?.simNumber,
      item?.sim_msisdn,
    ];
    const simNumber = simCandidates.find((v) => v != null && String(v).trim() !== '' && String(v).trim() !== '-') || '';
    setSelectedSettingsDeviceId(deviceId);
    setSelectedSettingsUserName(rowName);
    setSelectedSettingsSimNumber(String(simNumber).trim());
    setSettingsIframeUrl('');
    setShowSettingsIframe(false);
    setShowSettingsModal(true);
  };

  const handleSettingsDetailsToggle = async () => {
    if (showSettingsIframe) {
      setShowSettingsIframe(false);
      return;
    }

    if (settingsIframeUrl) {
      setShowSettingsIframe(true);
      return;
    }

    if (!selectedSettingsDeviceId) return;

    setSettingsIframeLoading(true);
    try {
      const response = await getDeviceDeeplink(selectedSettingsDeviceId);
      if (response?.data?.success && response?.data?.data?.redirectUrl) {
        setSettingsIframeUrl(response.data.data.redirectUrl);
        setShowSettingsIframe(true);
      }
    } catch (error) {
      console.error('Failed to load device deeplink:', error);
      setReportError('Unable to open device details iframe.');
    } finally {
      setSettingsIframeLoading(false);
    }
  };

  const buildReportPayload = (
    payloadPage = page,
    payloadSort = sortConfig,
    payloadSorts = sortConfigs,
    payloadLimit = itemsPerPage,
  ) => ({
    filters: {
      entity_id: filters.entityId ? Number(filters.entityId) : undefined,
      client_id: filters.clientId ? Number(filters.clientId) : undefined,
      period: {
        from: filters.periodFrom,
        to: filters.periodTo,
      },
      subscription_status: filters.subscriptionStatus || undefined,
    },
    page: payloadPage,
    limit: Number(payloadLimit),
    sort: (payloadSorts?.length
      ? payloadSorts
      : (payloadSort?.key ? [payloadSort] : [])
    ).map((entry) => ({ field: entry.key, dir: entry.direction })),
  });

  const buildExportPayload = () => ({
    filters: {
      entity_id: filters.entityId ? Number(filters.entityId) : undefined,
      client_id: filters.clientId ? Number(filters.clientId) : undefined,
      period: {
        from: filters.periodFrom,
        to: filters.periodTo,
      },
      subscription_status: filters.subscriptionStatus || undefined,
    },
    format: 'xlsx',
    sort: (sortConfigs?.length
      ? sortConfigs
      : (sortConfig?.key ? [sortConfig] : [])
    ).map((entry) => ({ field: entry.key, dir: entry.direction })),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });

  const toHeaderLabel = (key) => key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatDateOnly = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${month}/${day}/${year}`;
  };

  const runEnrollmentStatusReport = async (
    targetPage = 1,
    targetSort = sortConfig,
    targetSorts = sortConfigs,
    targetLimit = itemsPerPage,
  ) => {
    setReportLoading(true);
    setReportError('');
    try {
      const payload = buildReportPayload(targetPage, targetSort, targetSorts, targetLimit);
      const response = await fetchEnrollmentStatusReport(REPORT_CODE_ENROLLMENT, payload);
      const reportData = response?.data?.data || {};
      const columnsMeta = Array.isArray(reportData?.columns) ? reportData.columns : [];
      const items = Array.isArray(reportData?.items)
        ? reportData.items
        : Array.isArray(reportData?.rows)
          ? reportData.rows
          : Array.isArray(reportData?.records)
            ? reportData.records
          : Array.isArray(reportData)
            ? reportData
            : [];

      const keysFromColumns = columnsMeta
        .map((column) => column?.key)
        .filter(Boolean);
      const sample = items[0] || {};
      const primitiveKeys = keysFromColumns.length > 0
        ? keysFromColumns
        : Object.keys(sample).filter((key) => {
            const value = sample[key];
            return value === null || ['string', 'number', 'boolean'].includes(typeof value);
          });
      const normalizedLabel = (value) => String(value || '').trim().toLowerCase();
      const isDisplayIdColumn = (key) => {
        const columnMeta = columnsMeta.find((column) => column?.key === key);
        if (columnMeta?.label) return normalizedLabel(columnMeta.label) === 'id';
        return key === 'id';
      };
      const filteredKeys = primitiveKeys.filter((key) =>
        key !== 'gender' && !isDisplayIdColumn(key),
      );
      const emailKeyFromRows = ['email_address', 'email', 'emailAddress'].find((key) =>
        Object.prototype.hasOwnProperty.call(sample, key),
      );
      const hasEmailColumn = filteredKeys.includes('email_address')
        || filteredKeys.includes('email')
        || filteredKeys.includes('emailAddress');
      if (emailKeyFromRows && !hasEmailColumn) filteredKeys.push(emailKeyFromRows);

      const mappedRows = items.map((item, index) => {
        const simFromItem = [
          item?.msisdn,
          item?.sim_number,
          item?.simNumber,
          item?.sim_msisdn,
          item?.currentSim?.msisdn,
          item?.current_sim?.msisdn,
        ].find((v) => v != null && String(v).trim() !== '' && String(v).trim() !== '-');
        const row = {
          id: item?.id || item?.record_id || `${targetPage}-${index}`,
          __sourceId: item?.id ?? item?.record_id ?? null,
          __deviceId: item?.device_id || item?.deviceId || item?.device_db_id || item?.deviceDbId || null,
          __simNumber: simFromItem ? String(simFromItem).trim() : '',
        };
        filteredKeys.forEach((key) => {
          const rawValue = item?.[key];
          const isEnrollmentDateField = key === 'enrollment_date' || key === 'enrolment_date' || key === 'enrollmentDate' || key === 'enrolmentDate';
          row[key] = isEnrollmentDateField ? formatDateOnly(rawValue) : (rawValue ?? '-');
        });
        return row;
      });

      const mappedColumns = filteredKeys.map((key) => {
        const columnMeta = columnsMeta.find((column) => column?.key === key);
        const hasColumnsMeta = columnsMeta.length > 0;
        const headerLabel = columnMeta?.label || toHeaderLabel(key);
        const normalizedHeader = normalizedLabel(headerLabel);
        const isCustomerIdColumn = normalizedHeader === 'customer id'
          || key === 'customer_id'
          || key === 'customerId'
          || key === 'customerid';
        return {
          header: headerLabel,
          accessor: key,
          sortable: hasColumnsMeta ? columnMeta?.sortable === true : true,
          sortKey: key,
          searchKey: columnMeta?.searchable ? key : undefined,
          render: isCustomerIdColumn
            ? (item) => (
                <Button
                  variant="link"
                  className="p-0 text-primary fw-bold text-decoration-underline"
                  onClick={() => handleOpenDeviceSettings(item)}
                >
                  {item[key]}
                </Button>
              )
            : undefined,
        };
      });

      setReportRows(mappedRows);
      setReportColumns(mappedColumns);
      setPage(targetPage);
      setTotalPages(reportData?.pagination?.pages || 1);
      setSortConfig(targetSort);
      setSortConfigs(targetSorts || []);
      setItemsPerPage(Number(targetLimit));
      setHasRunReport(true);
      setShowFiltersPanel(false);
    } catch (error) {
      console.error('Failed to run enrollment status report:', error);
      setReportRows([]);
      setReportColumns([]);
      setReportError(error?.response?.data?.message || 'Failed to run report.');
    } finally {
      setReportLoading(false);
    }
  };

  const handleRunClick = () => {
    runEnrollmentStatusReport(1, sortConfig, sortConfigs);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'entityId' ? { clientId: '' } : {}),
    }));
  };
  const handleDateRangeChange = (selection) => {
    const nextStartDate = parseDateValue(selection?.startDate);
    const nextEndDate = parseDateValue(selection?.endDate);
    if (!nextStartDate || !nextEndDate) return;
    setFilters((prev) => ({
      ...prev,
      periodFrom: formatDate(nextStartDate),
      periodTo: formatDate(nextEndDate),
    }));
  };

  const handleSort = (key) => {
    const existingIndex = sortConfigs.findIndex((entry) => entry.key === key);
    const currentDirection = existingIndex >= 0
      ? sortConfigs[existingIndex].direction
      : (sortConfig.key === key ? sortConfig.direction : 'desc');
    const nextDirection = currentDirection === 'asc' ? 'desc' : 'asc';
    const nextSort = { key, direction: nextDirection };

    const remainingSorts = existingIndex >= 0
      ? sortConfigs.filter((entry) => entry.key !== key)
      : sortConfigs;
    const nextSorts = [nextSort, ...remainingSorts];

    runEnrollmentStatusReport(page, nextSort, nextSorts);
  };

  const handlePageChange = (nextPage) => {
    runEnrollmentStatusReport(nextPage, sortConfig, sortConfigs);
  };

  const openFiltersModal = () => {
    setShowFiltersPanel(true);
  };

  const handleLimitChange = (event) => {
    const nextLimit = Number(event.target.value);
    if (hasRunReport) {
      const nextSort = { ...sortConfig };
      runEnrollmentStatusReport(1, nextSort, sortConfigs, nextLimit);
    } else {
      setItemsPerPage(nextLimit);
    }
  };

  const getFileNameFromResponse = (response, fallback = `${reportCode || 'report'}-export.xlsx`) => {
    const contentDisposition = response?.headers?.['content-disposition'] || '';
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    const extracted = utf8Match?.[1] || plainMatch?.[1];
    if (!extracted) return fallback;
    try {
      return decodeURIComponent(extracted);
    } catch {
      return extracted;
    }
  };

  const handleExportReport = async () => {
    if (!reportCode) return;
    setExportLoading(true);
    setReportError('');
    try {
      const payload = buildExportPayload();
      const response = await exportEnrollmentStatusReport(reportCode, payload);
      const fileName = getFileNameFromResponse(response);
      const blobType = response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: blobType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export enrollment report:', error?.message);
      setReportError(error?.message || 'Failed to export report.');
    } finally {
      setExportLoading(false);
    }
  };
  return (
    <>
      {reportError && <Alert variant="danger">{reportError}</Alert>}
      {(!isEnrollmentFlow || !showFiltersPanel) && (
        <>
          <ManagementList
            title="Enrollment Status Report"
            data={reportRows}
            columns={reportColumns}
            addNewLink={null}
            isServerSide={true}
            isLoading={reportLoading}
            serverPage={page}
            serverTotalPages={totalPages}
            serverCounts={{ all: reportRows.length }}
            onPageChange={handlePageChange}
            onSort={handleSort}
            sortConfig={sortConfig}
            customTabs={[{ eventKey: 'all', title: 'Results' }]}
            customFilter={() => true}
            limit={itemsPerPage}
            handleLimitChange={handleLimitChange}
            showSearchFilterBtn={false}
            showSearchBar={false}
            openFiltersModal={openFiltersModal}
            onExport={handleExportReport}
            exportTooltip={exportLoading ? 'Exporting...' : 'Export report'}
            exportDisabled={reportLoading || exportLoading || !hasRunReport}
            canExport={canExportEnrollmentReport}
          />
        </>
      )}

      {isEnrollmentFlow && showFiltersPanel && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Header className="bg-primary text-white py-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h6 className="mb-1">Enrollment Status Report</h6>
                <p className="mb-0 text-white-50 small">Build your report with scope and period filters.</p>
              </div>
              <span className="badge text-bg-light">Report Builder</span>
            </div>
          </Card.Header>
          <Card.Body className="p-3 p-md-4">
            <div className="border rounded-3 p-3 p-md-4 bg-white">
              <div className="fw-semibold text-primary mb-1">Period</div>
              <p className="text-muted small mb-3">Select report date range.</p>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group controlId="reportDateRange">
                    <Form.Label>Date</Form.Label>
                    <CustomDateRangeFilter onChange={handleDateRangeChange} showDates />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="border rounded-3 p-3 p-md-4 bg-light mb-3">
              <div className="fw-semibold text-primary mb-1">Scope</div>
              <p className="text-muted small mb-3">Choose entity and client to narrow down report records.</p>
              <Row className="g-3">
                {visibility.entity && (
                  <Col md={6}>
                    <Form.Group controlId="reportEntity">
                      <Form.Label>Entity</Form.Label>
                      <Form.Select
                        value={filters.entityId}
                        onChange={(e) => handleFilterChange('entityId', e.target.value)}
                        disabled={loadingEntities}
                      >
                        <option value="">Select entity...</option>
                        {entitiesList.map((entity) => (
                          <option key={entity.id} value={entity.id}>
                            {entity.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}

                {visibility.client && (
                  <Col md={6}>
                    <Form.Group controlId="reportClient">
                      <Form.Label>Client</Form.Label>
                      <Form.Select
                        value={filters.clientId}
                        onChange={(e) => handleFilterChange('clientId', e.target.value)}
                        disabled={loadingEntities || loadingClients || (visibility.entity && !filters.entityId)}
                      >
                        <option value="">All clients</option>
                        {availableClients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <Form.Group controlId="reportSubscriptionStatus">
                    <Form.Label>Subscription Status</Form.Label>
                    <Form.Select
                      value={filters.subscriptionStatus}
                      onChange={(e) => handleFilterChange('subscriptionStatus', e.target.value)}
                    >
                      {subscriptionStatuses.map((status) => (
                        <option key={status.value || 'default'} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="d-flex flex-wrap justify-content-end gap-2 mt-4 pt-2 border-top">
              <Button variant="outline-secondary" className="px-4" onClick={closeEnrollmentStatusFilters}>
                Cancel
              </Button>
              <Button variant="primary" className="px-4" onClick={handleRunClick}>
                Run Your Report
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Modal
        show={showSettingsModal}
        onHide={closeSettingsModal}
        size="xl"
        dialogClassName="modal-device-settings-dialog"
        fullscreen="lg-down"
        centered
      >
        <Modal.Body className="p-0 device-settings-modal__body">
          <button
            type="button"
            className="device-settings-modal__floating-close"
            onClick={closeSettingsModal}
            aria-label="Close device settings"
          >
            ×
          </button>
          <div className="device-settings-modal__info-banner">
            <InfoCircle size={18} className="device-settings-modal__info-icon" />
            <p className="mb-0" style={{ fontSize: '16px' }}>
              {settingsIframeLoading
                ? 'Loading detailed view...'
                : showSettingsIframe
                  ? 'Current and historical details are visible. To switch back to the past 24 hours view '
                  : 'The device information displayed below is limited to the past 24 hours. To access current and historical data '}
              <button
                type="button"
                className="device-settings-modal__info-link device-settings-modal__info-link--cta"
                onClick={handleSettingsDetailsToggle}
                disabled={settingsIframeLoading}
              >
                click here
              </button>
            </p>
          </div>
          <div className="device-settings-modal__content">
            {showSettingsIframe && settingsIframeUrl ? (
              <iframe
                key={settingsIframeUrl}
                title="Device settings"
                src={settingsIframeUrl}
                className="w-100 h-100 border-0 d-block"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            ) : (
              <DeviceSettings
                deviceId={selectedSettingsDeviceId}
                fallbackFullName={selectedSettingsUserName}
                fallbackSimNumber={selectedSettingsSimNumber}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Reports;
